import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import EventModal from "@/components/EventModal";
import { Archive as ArchiveIcon, Calendar, Eye } from "lucide-react";

export default function Archive() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/events/archive").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openEvent = (ev) => {
    setActive(ev); setOpen(true);
    api.post(`/events/${ev.id}/access`).then(load).catch(() => {});
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

  return (
    <main className="bg-onyx min-h-screen pt-16" data-testid="archive-page">
      <div className="border-b border-white/10 grain bg-surface">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4 flex items-center gap-2"><ArchiveIcon size={16} /> Article Archive</p>
          <h1 className="font-serif text-4xl md:text-6xl text-bone mb-4">Accessed Articles</h1>
          <p className="text-bone/55 max-w-2xl text-lg">Every article the community has opened is preserved here, most recently read first.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-16">
        {loading ? (
          <p className="text-bone/40 py-20 text-center">Loading archive...</p>
        ) : items.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-white/10" data-testid="archive-empty">
            <ArchiveIcon className="text-bone/20 mx-auto mb-4" size={48} strokeWidth={1} />
            <p className="text-bone/50">No articles have been accessed yet. Open an article from "What's Happening Now" and it will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="archive-grid">
            {items.map((e) => (
              <button key={e.id} onClick={() => openEvent(e)}
                className="group bg-surface border border-white/10 text-left hover:border-gold/40 transition-colors flex flex-col overflow-hidden" data-testid="archive-card">
                {e.image_url && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={e.image_url} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-widest text-black bg-gold px-2.5 py-0.5">{e.tag}</span>
                    <span className="text-bone/40 text-xs font-mono flex items-center gap-1"><Calendar size={11} /> {e.date}</span>
                  </div>
                  <h3 className="font-serif text-xl text-bone mb-2 leading-snug">{e.title}</h3>
                  <p className="text-bone/55 text-sm leading-relaxed flex-1">{e.summary}</p>
                  <div className="mt-4 flex items-center justify-between text-xs text-bone/40">
                    <span className="flex items-center gap-1.5"><Eye size={12} /> {e.access_count} view{e.access_count === 1 ? "" : "s"}</span>
                    <span>Last read {fmt(e.last_accessed)}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <EventModal event={active} open={open} onClose={setOpen} />
    </main>
  );
}
